import { t } from "../../../i18n";
import { tEngine } from "../../../i18n/engine";
import { costEstimate, DEFAULT_PRICING_NOTE, fmt, type QuantitiesResult, type WallInput } from "@tasduvar/engine";
import type { ReportModel } from "../model";
import { Section } from "./common";

const unitLabel = (u: string): string => (u === "adet" ? t("adet") : u);

/** 13. Metraj (duvar düzeyi): metraj tablosu, donatı metrajı (betonarme), kabuller. */
export function QuantitiesSection({ wall, q }: { wall: WallInput; q: QuantitiesResult }): JSX.Element {
  return (
    <Section id="quantities" title={t("13. Metraj")}>
      <p className="muted">{t("Miktarlar 1 m duvar uzunluğu için kesit geometrisinden; toplam duvar boyu")} L = {fmt(q.length, 1)} m ({wall.name}). {t("Bilgi satırları (↳) keşfe girmez.")}</p>
      <table>
        <thead><tr><th>{t("İmalat")}</th><th>{t("Birim")}</th><th className="num">{t("1 m için")}</th><th className="num">{t("Toplam")}</th><th>{t("Hesap")}</th></tr></thead>
        <tbody>
          {q.lines.map((l) => (
            <tr key={l.key} className={l.info ? "muted" : undefined}><td>{tEngine(l.label)}</td><td>{unitLabel(l.unit)}</td><td className="num">{fmt(l.perMetre, l.unit === "kg" ? 1 : 3)}</td><td className="num">{fmt(l.total, l.unit === "kg" ? 0 : 2)}</td><td className="muted">{tEngine(l.formula)}</td></tr>
          ))}
        </tbody>
      </table>
      {q.rebar ? (<>
        <h3>{t("Donatı metrajı")}</h3>
        <table>
          <thead><tr><th>{t("Konum")}</th><th className="num">φ</th><th className="num">{t("Adet / m")}</th><th className="num">{t("Boy (m)")}</th><th className="num">kg / m</th><th className="num">{t("Toplam (kg)")}</th><th>{t("Boy hesabı")}</th></tr></thead>
          <tbody>
            {q.rebar.byPosition.map((b, i) => <tr key={`${b.position}-${i}`}><td>{tEngine(b.label)}</td><td className="num">φ{b.dia}</td><td className="num">{fmt(b.countPerMetre, 2)}</td><td className="num">{fmt(b.length, 2)}</td><td className="num">{fmt(b.kgPerMetre, 1)}</td><td className="num">{fmt(b.total, 0)}</td><td className="muted">{tEngine(b.note)}</td></tr>)}
            <tr><td colSpan={4}><strong>{t("Toplam")}</strong></td><td className="num"><strong>{fmt(q.rebar.kgPerMetre, 1)}</strong></td><td className="num"><strong>{fmt(q.rebar.total, 0)}</strong></td><td /></tr>
          </tbody>
        </table>
        <table>
          <thead><tr><th>{t("Çap")}</th>{q.rebar.byDiameter.map((d) => <th key={d.dia} className="num">φ{d.dia}</th>)}<th className="num">{t("Toplam")}</th></tr></thead>
          <tbody>
            <tr><td>kg / m</td>{q.rebar.byDiameter.map((d) => <td key={d.dia} className="num">{fmt(d.kgPerMetre, 1)}</td>)}<td className="num">{fmt(q.rebar.kgPerMetre, 1)}</td></tr>
            <tr><td>{t("Toplam (kg)")}</td>{q.rebar.byDiameter.map((d) => <td key={d.dia} className="num">{fmt(d.total, 0)}</td>)}<td className="num">{fmt(q.rebar.total, 0)}</td></tr>
          </tbody>
        </table>
      </>) : null}
      <h3>{t("Kabuller")}</h3>
      <ul>{q.assumptions.map((a, i) => <li key={i}>{tEngine(a)}</li>)}</ul>
    </Section>
  );
}

/** 13b. Keşif özeti (proje düzeyi): tüm duvarların metrajı × birim fiyat (KDV hariç). */
export function CostSection({ m }: { m: ReportModel }): JSX.Element | null {
  const qs = m.walls.map((w) => w.quantities).filter((q): q is QuantitiesResult => !!q);
  if (!qs.length) return null;
  const c = costEstimate(qs, m.project.pricing);
  const cur = c.currency;
  return (
    <Section id="cost" title={t("13b. Keşif Özeti")}>
      <p className="muted">{t("Toplam miktarlar rapordaki duvarların toplamıdır")} ({m.walls.filter((w) => w.quantities).map((w) => `${w.wall.name} L = ${fmt(w.quantities!.length, 1)} m`).join(", ")}). {t("Birim fiyatlar KDV hariç.")}{m.project.pricing.year ? ` ${t("Fiyat yılı:")} ${m.project.pricing.year}.` : ""}</p>
      <table>
        <thead><tr><th>{t("Poz no")}</th><th>{t("Tanım")}</th><th>{t("Birim")}</th><th className="num">{t("Miktar")}</th><th className="num">{t("Birim fiyat")} ({cur})</th><th className="num">{t("Tutar")} ({cur})</th></tr></thead>
        <tbody>
          {c.rows.map((r) => <tr key={r.key}><td className="mono">{r.code || "—"}</td><td>{tEngine(r.label)}</td><td>{unitLabel(r.unit)}</td><td className="num">{fmt(r.quantity, r.unit === "t" ? 3 : 2)}</td><td className="num">{r.unitPrice > 0 ? fmt(r.unitPrice, 2) : "—"}</td><td className="num">{r.amount !== null ? fmt(r.amount, 2) : "—"}</td></tr>)}
          <tr><td colSpan={5}><strong>{t("Toplam (KDV hariç)")}</strong></td><td className="num"><strong>{c.priced ? fmt(c.total, 2) : "—"}</strong></td></tr>
        </tbody>
      </table>
      {c.missing.length ? <p className="muted">{t("Birim fiyatı girilmemiş pozlar toplama dâhil değildir:")} {c.missing.length}.</p> : null}
      {c.note ? <p className="note"><strong>{t("Not:")}</strong> {c.note === DEFAULT_PRICING_NOTE ? t(DEFAULT_PRICING_NOTE) : c.note}</p> : null}
    </Section>
  );
}
